import { useEffect, useState } from 'react'
import { onAuthStateChanged } from 'firebase/auth'
import { auth } from './firebase'
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import DashboardPage from './pages/DashboardPage'
import ChildDetailPage from './pages/ChildDetailPage'
import AdminPage from './pages/AdminPage'
import AccountPage from './pages/AccountPage'
import DownloadsPage from './pages/DownloadsPage'
import Toaster from './components/Toaster'
import PaymentOptions from './components/PaymentOptions'
import { subscribeIsAdmin, subscribeParent } from './lib/db'
import { effectiveStatus, daysRemaining } from './lib/billing'
import type { Tab } from './lib/nav'
import type { BillingInfo } from './lib/types'

type View =
  | { kind: 'landing' }
  | { kind: 'login' }
  | { kind: 'register' }
  | { kind: 'dashboard' }
  | { kind: 'child'; uid: string; tab: Tab }
  | { kind: 'admin' }
  | { kind: 'account' }
  | { kind: 'downloads' }

export default function App() {
  const [authStatus, setAuthStatus] = useState<'loading' | 'signedOut' | 'signedIn'>('loading')
  const [view, setView] = useState<View>({ kind: 'landing' })
  const [isAdmin, setIsAdmin] = useState(false)
  const [billing, setBilling] = useState<BillingInfo | null>(null)
  const [uid, setUid] = useState<string | null>(null)
  const [permsLoaded, setPermsLoaded] = useState(false)

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid ?? null)
      setAuthStatus(user ? 'signedIn' : 'signedOut')
      setView(user ? { kind: 'dashboard' } : { kind: 'landing' })
    })
    return unsub
  }, [])

  useEffect(() => {
    if (!uid) {
      setPermsLoaded(false)
      return
    }
    setPermsLoaded(false)
    let adminOk = false
    let billingOk = false
    const done = () => {
      if (adminOk && billingOk) setPermsLoaded(true)
    }
    const un1 = subscribeIsAdmin(uid, (v) => {
      setIsAdmin(v)
      adminOk = true
      done()
    })
    const un2 = subscribeParent(uid, (p) => {
      setBilling(p?.billing ?? null)
      billingOk = true
      done()
    })
    return () => {
      un1()
      un2()
    }
  }, [uid])

  if (authStatus === 'loading') {
    return (
      <div className="flex min-h-full items-center justify-center bg-slate-100">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    )
  }

  // ---------- No autenticado: landing / login / registro ----------
  if (authStatus === 'signedOut') {
    if (view.kind === 'login') {
      return (
        <>
          <LoginPage
            onAuthed={() => {
              setAuthStatus('signedIn')
              setView({ kind: 'dashboard' })
            }}
            onRegister={() => setView({ kind: 'register' })}
            onBack={() => setView({ kind: 'landing' })}
          />
          <Toaster />
        </>
      )
    }
    if (view.kind === 'register') {
      return (
        <>
          <RegisterPage
            onAuthed={() => {
              setAuthStatus('signedIn')
              setView({ kind: 'dashboard' })
            }}
            onBackToLogin={() => setView({ kind: 'login' })}
          />
          <Toaster />
        </>
      )
    }
    return (
      <>
        <LandingPage
          onLogin={() => setView({ kind: 'login' })}
          onRegister={() => setView({ kind: 'register' })}
        />
        <Toaster />
      </>
    )
  }

  // ---------- Autenticado ----------
  // Esperar rol + billing antes de decidir la puerta: sin esto, un padre
  // expirado/suspendido veía el Dashboard un instante (billing=null → 'trial').
  if (!permsLoaded) {
    return (
      <div className="flex min-h-full items-center justify-center bg-slate-100">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    )
  }
  // El admin/dueño siempre es 'active': sin puertas ni banners de plan.
  const billStatus = effectiveStatus(billing ?? undefined, isAdmin)

  if (view.kind === 'admin') {
    if (!isAdmin) return (
      <>
        <DashboardPage
          isAdmin={false}
          onOpenAdmin={() => setView({ kind: 'dashboard' })}
          onOpenAccount={() => setView({ kind: 'account' })}
          onOpenDownloads={() => setView({ kind: 'downloads' })}
          onOpenChild={(childUid, tab) => setView({ kind: 'child', uid: childUid, tab: tab ?? 'apps' })}
          trialDays={billStatus === 'trial' ? daysRemaining(billing ?? undefined, isAdmin) : undefined}
        />
        <Toaster />
      </>
    )
    return (
      <>
        <AdminPage
          onOpenAccount={() => setView({ kind: 'account' })}
          onOpenDownloads={() => setView({ kind: 'downloads' })}
          onNavigate={() => setView({ kind: 'dashboard' })}
        />
        <Toaster />
      </>
    )
  }

  if (view.kind === 'account') {
    return (
      <>
        <AccountPage
          onBack={() => setView({ kind: 'dashboard' })}
          onOpenAdmin={() => setView({ kind: 'admin' })}
          onOpenDownloads={() => setView({ kind: 'downloads' })}
          onOpenChild={(childUid, childTab) =>
            setView({ kind: 'child', uid: childUid, tab: childTab ?? 'apps' })
          }
        />
        <Toaster />
      </>
    )
  }

  if (view.kind === 'downloads') {
    return (
      <>
        <DownloadsPage
          onBack={() => setView({ kind: 'dashboard' })}
          isAdmin={isAdmin}
          onOpenAdmin={() => setView({ kind: 'admin' })}
          onOpenAccount={() => setView({ kind: 'account' })}
        />
        <Toaster />
      </>
    )
  }

  if (view.kind === 'child') {
    return (
      <ChildDetailPage
        uid={view.uid}
        tab={view.tab}
        isAdmin={isAdmin}
        onBack={() => setView({ kind: 'dashboard' })}
        onOpenAdmin={() => setView({ kind: 'admin' })}
        onOpenAccount={() => setView({ kind: 'account' })}
        onOpenDownloads={() => setView({ kind: 'downloads' })}
        onOpenChild={(childUid, childTab) =>
          setView({ kind: 'child', uid: childUid, tab: childTab ?? 'apps' })
        }
      />
    )
  }

  // Puerta de suscripción (no aplica al admin)
  if (!isAdmin && billStatus === 'expired') {
    return (
      <>
        <RenewScreen />
        <Toaster />
      </>
    )
  }
  if (!isAdmin && billStatus === 'suspended') {
    return (
      <>
        <SuspendedScreen />
        <Toaster />
      </>
    )
  }

  return (
    <>
      <DashboardPage
        isAdmin={isAdmin}
        onOpenAdmin={() => setView({ kind: 'admin' })}
        onOpenAccount={() => setView({ kind: 'account' })}
        onOpenDownloads={() => setView({ kind: 'downloads' })}
        onOpenChild={(childUid, tab) => setView({ kind: 'child', uid: childUid, tab: tab ?? 'apps' })}
        trialDays={billStatus === 'trial' ? daysRemaining(billing ?? undefined, isAdmin) : undefined}
      />
      <Toaster />
    </>
  )
}



function RenewScreen() {
  return (
    <div className="min-h-full bg-gradient-to-br from-brand-600 via-brand-500 to-brand-700 p-4 py-10">
      <div className="mx-auto max-w-lg">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 text-3xl">
            ⏰
          </div>
          <h1 className="text-2xl font-bold text-white">Tu plan ha expirado</h1>
          <p className="mt-2 text-brand-100">
            Renueva tu suscripción para reactivar la protección de tus dispositivos.
            Elige un plan, paga y el administrador lo activará en minutos.
          </p>
        </div>

        <div className="rounded-3xl bg-white p-6 shadow-2xl">
          <PaymentOptions
            uid={auth.currentUser?.uid ?? ''}
            name="Cliente KidSafe"
            email={auth.currentUser?.email ?? ''}
          />
          <button
            onClick={() => auth.signOut()}
            className="mt-4 w-full rounded-xl border border-slate-200 py-2.5 text-sm text-slate-500 hover:bg-slate-50"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  )
}

function SuspendedScreen() {
  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-slate-100 p-6 text-center">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-xl">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-100 text-3xl">
          ⛔
        </div>
        <h1 className="text-xl font-bold text-slate-800">Cuenta suspendida</h1>
        <p className="mt-2 text-sm text-slate-500">
          Tu cuenta fue suspendida temporalmente. Contacta a tu administrador para más información.
        </p>
        <button
          onClick={() => auth.signOut()}
          className="mt-6 w-full rounded-xl bg-brand-600 py-3 font-semibold text-white hover:bg-brand-700"
        >
          Cerrar sesión
        </button>
      </div>
    </div>
  )
}